import React, { useEffect, useMemo, useState } from 'react';
import NewUsersByMonthChart from '../components/NewUsersByMonthChart';
import { SuperadminUserListItem, fetchUsers, deleteUser } from '../lib/superadminUsersApi';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import UserCard from '../components/UserCard';

// roleLabel was moved into UserCard; keeping no local copy here

const UsersIndex: React.FC = () => {
  const [search, setSearch] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<SuperadminUserListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ open: boolean; id?: number; email?: string }>({ open: false });
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setSearch(pendingSearch), 300);
    return () => clearTimeout(t);
  }, [pendingSearch]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchUsers({ search, limit: 100, order: 'asc', sort: 'name' });
        setRows(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, [search]);

  const onDelete = (id: number, email: string) => setConfirm({ open: true, id, email });
  const confirmDelete = async () => {
    if (!confirm.id) return;
    try {
      await deleteUser(confirm.id);
      setRows((prev) => prev.filter((u: any) => (u as any).id !== confirm.id));
    } catch (e: any) {
      setError(e?.message || 'Delete failed');
    } finally {
      setConfirm({ open: false });
    }
  };

  const grid = useMemo(() => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {rows.map((u: any, idx) => (
        <UserCard key={`${u.email}-${idx}`} id={u.id} name={u.userName} email={u.email} avatarUrl={u.profilePicture} isAdmin={u.isAdmin} isSuperAdmin={u.isSuperAdmin} totalRecordings={u.totalRecordings} totalComments={u.totalComments} workspaces={u.workspaces} onView={(id) => navigate(`/users/${id}`)} onDelete={onDelete} />
      ))}
    </div>
  ), [rows, navigate]);

  return (
    <div className="space-y-6">
      <NewUsersByMonthChart />
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          <input
            className="border rounded px-3 py-2 w-full"
            placeholder="Search by name or email"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
          />
          <select className="border rounded px-3 py-2 text-sm text-gray-600">
            <option>Filter By</option>
          </select>
        </div>
      </div>
      {loading ? (
        <div>Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        grid
      )}
      <ConfirmDialog open={confirm.open} title="Delete User" message={`Are you sure you want to delete ${confirm.email}?`} onCancel={() => setConfirm({ open: false })} onConfirm={confirmDelete} />
    </div>
  );
};

export default UsersIndex;

